import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import ComicPagina from "./ComicPagina";
import ScrollToTop from "./ScrollToTop";
import { apiUrl } from "./mangaApi";

const ComicBase = lazy(() => import("./ComicBase"));
const ComicReader = lazy(() => import("./ComicReader"));
const BusquedaPrecisa = lazy(() => import("./BusquedaPrecisa"));
const Buscar = lazy(() => import("./Buscar"));
const Autores = lazy(() => import("./Autores"));
const Tags = lazy(() => import("./Tags"));
const Register = lazy(() => import("./Registrarse/Register"));
const Login = lazy(() => import("./Registrarse/Login"));
const MangaUpload = lazy(() => import("./MangaUpload"));
const ServerMangaPage = lazy(() => import("./ServerMangaPage"));

const GoogleAnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);

  return null;
};

// =====================================================
// 🚀 AUTO-DETECCIÓN DE CÓMICS DESDE /pages
// =====================================================
const context = require.context("./pages", true, /\.jsx$/);

const comicsAuto = context
  .keys()
  .map((file, index) => {
    try {
      const mod = context(file);
      const info = mod.infoManga;
      const Component = mod.default;

      if (!info) return null;

      const enlace =
        info.enlace ||
        `/comic/${info.titulo
          .toLowerCase()
          .replace(/\s+/g, "")
          .replace(/[^a-z0-9]/g, "")}`;

      return {
        id: index + 1,
        titulo: info.titulo,
        portada: info.portada,
        reverso: info.reverso || info.portada,
        enlace,
        fecha: info.fecha,
        info: info.info || {},
        Component,
      };
    } catch (e) {
      console.error("❌ Error al cargar:", file, e);
      return null;
    }
  })
  .filter(Boolean);

const completarMangaDelServidor = async (item) => {
  const completo =
    item.type &&
    item.language &&
    item.magazine !== undefined &&
    Array.isArray(item.tags) &&
    item.backCoverUrl;

  if (completo || !item.slug) return item;

  try {
    const response = await fetch(
      apiUrl(`/catalog/${encodeURIComponent(item.slug)}`)
    );
    if (!response.ok) return item;

    const detalle = await response.json();
    const paginas = Array.isArray(detalle.pages)
      ? detalle.pages
      : Array.isArray(detalle.previewPages)
        ? detalle.previewPages
        : [];

    return {
      ...item,
      type: item.type || detalle.type || "",
      author: item.author || detalle.author || "",
      artist: item.artist || detalle.artist || "",
      magazine: item.magazine ?? detalle.magazine ?? "",
      language: item.language || detalle.language || "",
      publicationGroup:
        item.publicationGroup || detalle.publicationGroup || "",
      tags: Array.isArray(item.tags)
        ? item.tags
        : Array.isArray(detalle.tags)
          ? detalle.tags
          : [],
      backCoverUrl:
        item.backCoverUrl ||
        detalle.backCoverUrl ||
        paginas[1] ||
        paginas[0] ||
        item.coverUrl,
    };
  } catch (error) {
    console.error("No se pudo completar la información del manga:", error);
    return item;
  }
};

// =====================================================
// 🚀 APP PRINCIPAL
// =====================================================
function App() {
  const [serverComics, setServerComics] = useState([]);

  useEffect(() => {
    let active = true;

    const loadCatalog = () => {
      fetch(apiUrl("/catalog"))
        .then((response) => {
          if (!response.ok) {
            throw new Error("No se pudo cargar el catálogo del servidor.");
          }
          return response.json();
        })
        .then(async (items) => {
          if (!active) return;
          const catalog = Array.isArray(items) ? items : [];
          const catalogoCompleto = await Promise.all(
            catalog.map(completarMangaDelServidor)
          );
          if (!active) return;
          setServerComics(
            catalogoCompleto.map((item) => ({
              id: `server-${item.mangaId}-${item.chapterId}`,
              nombre: item.displayTitle || item.title,
              imagen: item.coverUrl,
              titulo: item.title,
              portada: item.coverUrl,
              reverso: item.backCoverUrl || item.coverUrl,
              enlace:
                item.detailUrl ||
                `/manga/${item.slug}`,
              fecha: item.createdAt,
              autor: item.author || "",
              info: {
                artistas: item.artist || item.author || "",
                etiquetas: Array.isArray(item.tags) ? item.tags : [],
                idioma: item.language || "",
                tipo: item.type || "",
                serie: item.magazine || "",
                grupo: item.publicationGroup || "",
              },
              mangaId: item.mangaId,
              chapterId: item.chapterId,
              slug: item.slug,
              readerUrl: item.readerUrl,
              esPopular: Boolean(item.isPopular),
              popularAt: item.popularAt || item.createdAt,
              esServidor: true,
            }))
          );
        })
        .catch((error) => {
          if (active) {
            console.error("No se pudo cargar el catálogo del VPS:", error);
          }
        });
    };

    loadCatalog();
    window.addEventListener("tmoxnight:catalog-updated", loadCatalog);

    return () => {
      active = false;
      window.removeEventListener("tmoxnight:catalog-updated", loadCatalog);
    };
  }, []);

  const localComics = [...comicsAuto]
    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
    .map((m) => ({
      id: m.id,
      nombre: m.titulo,
      imagen: m.portada,
      titulo: m.titulo,
      portada: m.portada,
      reverso: m.reverso || m.portada,
      enlace: m.enlace,
      fecha: m.fecha,
      info: m.info || {},
    }));

  const comicsLista = [...serverComics, ...localComics];

  return (
    <Router>
      <GoogleAnalyticsTracker />
      <ScrollToTop />

      <Suspense
        fallback={
          <div
            role="status"
            style={{
              minHeight: "100vh",
              display: "grid",
              placeItems: "center",
              color: "#00efff",
              background: "#020909",
            }}
          >
            Cargando…
          </div>
        }
      >
        <Routes>
        {/* LOGIN */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/subir" element={<MangaUpload />} />

        {/* HOME + PAGINACIÓN */}
        <Route
          path="/"
          element={<ComicPagina comics={comicsLista} />}
        />

        <Route
          path="/page/:num"
          element={<ComicPagina comics={comicsLista} />}
        />

        {/* OTRAS RUTAS */}
        <Route path="/comic" element={<ComicBase />} />
        <Route
          path="/manga/:slug"
          element={<ServerMangaPage suggestions={comicsLista} />}
        />
        <Route path="/lector/:mangaId/:chapterId/:page" element={<ComicReader />} />
        <Route path="/lector/:comicId/:page" element={<ComicReader />} />
        <Route path="/lector/:id" element={<ComicReader />} />
        <Route path="/busqueda-precisa" element={<BusquedaPrecisa mangas={comicsLista} />} />
        <Route path="/autores" element={<Autores comics={comicsLista} />} />
        <Route path="/tags" element={<Tags comics={comicsLista} />} />
        <Route path="/buscar" element={<Buscar comics={comicsLista} />} />

        {/* CÓMICS AUTOMÁTICOS */}
        {comicsAuto.map((m, i) => (
          <Route key={i} path={m.enlace} element={<m.Component />} />
        ))}
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
